import { CheckIcon, ChevronDoubleLeftIcon, PencilIcon, PlusIcon, StarIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import Button from "../../components/Button";
import { LoadingMask } from "../../components/LoadingMask";
import { Toast } from "../../components/Toast";
import { Form } from "../../components/form/Form";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { Agency, BillingPeriod } from "../../models/Agency";
import {
  createAgencyBillingPeriod,
  createAgencyTwilioConfig,
  endAgencyBillingPeriod,
  obtainAuthorizationToken,
  updateAgency,
  updateAgencyTwilioConfig,
} from "../../state/agencies/action";
import { AppDispatch, RootState } from "../../state/store";
import { setCurrentUserAgency } from "../../state/users/actions";
import { ReactiveFormInputArea } from "../../components/form/ReactiveFormInputArea";
import FilteredTable from "../../components/FilteredTable";
import { DocusignConfig } from "../../models/DocusignConfig";
import { AgencyDocusignConfigModal } from "../../components/agency/docuSignConfig/AgencyDocusignConfigModal";
import Table from "../../components/Table";
import { SetAgencyMainDocusignConfigModal } from "../../components/agency/docuSignConfig/SetAgencyMainDocusignConfigModal";
import { deleteAgencyDocusignConfig, setAgencyMainDocusignConfig } from "../../state/docusignConfig/actions";
import { AgencyDeleteDocusignConfigModal } from "../../components/agency/docuSignConfig/AgencyDeleteDocusignConfigModal";
import { EditAgencyAvailableMarkteModal } from "../../components/agency/policyTypes/EditAgencyAvailableMarketModal";
import { AgencyPolicyType } from "../../models/PolicyMarket";
import { MailType } from "../../models/Mail";
import { deleteAgencyMailType } from "../../state/mail/actions";
import { AgencyMailTypeModal } from "../../components/agency/mail/AgencyMailTypeModal";
import { DeleteAgencyMailTypeModal } from "../../components/agency/mail/DeleteAgencyMailTypeModal";
import { ObtainAuthorizationTokenModal } from "../../components/agency/token/ObtainAuthorizationTokenModal";
import { RoleEnum } from "../../enums/Role";
import classNames from "classnames";
import { AgencyFormSection, AgencyFormSectionEnum, getAgencyFormSections } from "../../enums/AgencyFormSection";
import { AgencyInformationModule } from "../../components/agency/AgencyInformationModule";
import { AuthorizationTokenModule } from "../../components/agency/token/AuthorizationTokenModule";
import { AgencyBillingModule } from "../../components/agency/billing/AgencyBillingModule";
import { AgencyAvailableMarketModule } from "../../components/agency/policyTypes/AgencyAvailableMarketModule";
import { AgencyMailTypeModule } from "../../components/agency/mail/AgencyMailTypeModule";
import { AgencyTwilioConfigModule } from "../../components/agency/twilio/AgencyTwilioConfigModule";
import { AgencyDocusignConfigModule } from "../../components/agency/docuSignConfig/AgencyDocusignConfigModule";
import { AgencyDocusignTemplatesModule } from "../../components/agency/docusignTemplates/AgencyDocusignTemplatesModule";

export const AgencyDetails = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const currentAgency = useSelector((state: any) => state.users.currentUser?.agency);
  const dispatch = useDispatch<AppDispatch>();
  const isUserLoading = useSelector((state: any) => state.users.isLoading);
  const isAgencyLoading = useSelector((state: any) => state.agencies.isLoading);

  const [isFormSelected, setIsFormSelected] = useState<boolean>(false)
  const [sectionSelected, setSectionSelected] = useState<AgencyFormSectionEnum>(AgencyFormSectionEnum.INFORMATION)
  const sections = getAgencyFormSections()

  const isLoading = useMemo(() => {
    return (isUserLoading && isAgencyLoading);
  }, [isUserLoading, isAgencyLoading]);

  const selectFormSection = (section: AgencyFormSection) => {
    setSectionSelected(section.key)
    setIsFormSelected(true)
  }

  return (
    <>
      {isLoading && <LoadingMask />}
      <ToastContainer progressStyle={{ background: "#D4AF37" }} />
      <div key={12883} className="m-5 flex gap-5">
        <div className={classNames({
          "bg-white rounded-xl p-3 shadow w-full lg:w-1/3 xl:w-1/4 h-fit": true,
          "hidden": isFormSelected,
          "block": !isFormSelected,
          "lg:block": true
        })}>
          {sections.map((section, index) =>
          (<div key={index}>
            <button onClick={() => selectFormSection(section)} type="button"
              className={classNames({
                "w-full py-4 px-3 rounded-lg flex flex-row items-center justify-between": true,
                "bg-neutral-200": sectionSelected === section.key,
                "hover:bg-neutral-300": sectionSelected !== section.key
              })} >
              <div className="font-semibold">{section.label}</div>
            </button>
            {index !== (sections.length - 1) && <div className="w-full h-0.5 bg-neutral-200"></div>}
          </div>
          )
          )}
        </div>

        <div className={classNames({
          "w-full bg-white rounded-xl p-3 shadow": true,
          "hidden": !isFormSelected,
          "block": isFormSelected,
          "lg:block": true
        })} >
          <div className="flex flex-col justify-start gap-3">
            <Button
              isTerciary
              onClick={() => setIsFormSelected(false)}
              iconOnly
              classNames="w-9 p-1 aspect-square mr-4 lg:hidden"
            >
              <ChevronDoubleLeftIcon />
            </Button>
            { sectionSelected === AgencyFormSectionEnum.INFORMATION && <AgencyInformationModule />}
            { sectionSelected === AgencyFormSectionEnum.AUTHORIZATION_TOKEN && <AuthorizationTokenModule currentAgency={currentAgency}  />}
            { sectionSelected === AgencyFormSectionEnum.BILLING && <AgencyBillingModule currentAgency={currentAgency}  />}
            { sectionSelected === AgencyFormSectionEnum.AVAILABLE_MARKETS && <AgencyAvailableMarketModule />}
            { sectionSelected === AgencyFormSectionEnum.PHYSICAL_MAIL_TYPES && <AgencyMailTypeModule currentAgency={currentAgency}  />}
            { sectionSelected === AgencyFormSectionEnum.TWILIO_CONFIG && <AgencyTwilioConfigModule />}
            { sectionSelected === AgencyFormSectionEnum.DOCUSIGN_CONFIG && <AgencyDocusignConfigModule currentAgency={currentAgency} /> }
            { sectionSelected === AgencyFormSectionEnum.DOCUSIGN_TEMPLATES && <AgencyDocusignTemplatesModule /> }
          </div>
        </div>
      </div>
    </>
  );
};
